/**
 * Leads / Contactos — quien ha preguntado por la clínica.
 *
 * Un listado donde se va apuntando a quien contacta: nombre, teléfono, correo
 * y motivo de la consulta; lo que no dio se queda en blanco. Cualquiera de
 * administración puede contactar cualquier lead y, al hacerlo, se marca como
 * contactado y sale tachado en rojo.
 *
 * Tres decisiones de esta pantalla:
 *
 *  · LO QUE NO SE SABE SE QUEDA EN BLANCO. Ni «—», ni «sin nombre», ni un
 *    relleno: el hueco vacío es el dato. Quien mira la fila tiene que poder
 *    distinguir «no lo dio» de cualquier cosa que hayamos escrito nosotros.
 *  · LO PENDIENTE, DELANTE. Los contactados bajan, tachados y en rojo, pero no
 *    desaparecen: la lista es también el registro de quién preguntó.
 *  · SI LA CONSULTA FALLA, SE DICE. Una lista vacía porque el servidor no
 *    contestó se lee como «no ha llamado nadie», que es enseñar un error como
 *    si fuera un dato.
 *
 * Y de qué MARCA viene cada uno. Las marcas las da de alta cada clínica en
 * Configuración y las manda el servidor con la lista (`marcas`); ninguna está
 * escrita aquí. La marca de cada lead la decide el servidor con UNA regla
 * (services/marca_lead.py: a mano; si no, el primer buzón de Mail de una marca
 * al que escribió; si no, lo que diga «de dónde viene»); aquí se pinta: una
 * etiqueta de color en cada fila, pestañas con su número arriba y la ficha,
 * donde se asigna a mano lo que sale «Sin identificar». Sin marcas dadas de
 * alta no hay pestañas ni etiquetas: la lista se ve como siempre.
 *
 * Y lo que sale de la lista SIN BORRARSE: los DESCARTADOS (se descartan desde
 * la ficha, con un motivo, y se recuperan) y los YA CITADOS (su teléfono o su
 * correo es el de un paciente con cita; lo sabe el servidor). Cada uno tiene
 * su filtro con su número, y ninguno cuenta en «sin contactar · contactados»
 * ni en las pestañas de la lista.
 *
 * Y en bloque: una casilla por fila y otra arriba para todos los que se ven.
 * «Eliminar de la lista» los DESCARTA (se recuperan en «Descartados»), y en
 * «Descartados» la misma selección los recupera.
 *
 * Y de dónde viene cada uno, su PROCEDENCIA: una campaña de un tratamiento o
 * una de las demás vías. La decide el servidor (services/procedencia_lead.py);
 * aquí se pinta en su columna, se filtra con su número (las campañas primero,
 * luego las demás vías), se ordena por ella y se pone a mano en la ficha o al
 * añadir. Es otra cosa que la marca, y los dos filtros se combinan. El filtro
 * y el orden van en la URL (?procedencia=campana:<tratamiento>&orden=procedencia)
 * para poder enlazarlos desde Indicadores.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { api } from '../lib/api';
import { error as logError } from '../lib/logger';
import { PageHeader, EmptyState } from '../components/Common';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Phone, Mail, Plus, Check, Undo2, Loader2, Trash2, UserPlus, FileText, Archive, ArchiveRestore, CalendarCheck,
  ArrowDownWideNarrow,
} from 'lucide-react';
import { toast } from 'sonner';
import { normalizaTexto, telefonoConPrefijo, telefonoParaEnlace } from '../lib/utils';
import { useAuth } from '../lib/auth';
import { isManagement } from '../lib/roles';
import MarcaLead, {
  SIN_IDENTIFICAR, colorDeMarca, estiloMarca, nombreMarca,
} from '../components/MarcaLead';
import ProcedenciaLead, {
  SIN_OPCIONES_PROCEDENCIA, SelectorProcedencia, esCampana, estiloProcedencia,
} from '../components/ProcedenciaLead';

function cuando(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('es-ES',
    { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function mensajeError(e, porDefecto) {
  return e?.response?.data?.detail || porDefecto;
}

/** «2026-10-05» y «10:00» → «05/10/2026 10:00», sin pasar por la zona horaria. */
export function fechaDeCita(fecha, hora) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(fecha || '');
  if (!m) return '';
  return `${m[3]}/${m[2]}/${m[1]}${hora ? ` ${hora}` : ''}`;
}

// Los filtros de estado de la lista (27/09/2026): la de siempre, los que ya
// tienen cita y los descartados.
const VISTAS = ['lista', 'citados', 'descartados'];

const VACIO = {
  nombre: '', telefono: '', email: '', motivo: '', origen: '', marca: '', procedencia: '',
};

/** Qué se dice cuando una pestaña de marca no tiene a nadie. */
function vacioDeMarca(clave, marcas) {
  if (clave === 'todos') return 'Todavía no hay contactos';
  if (clave === 'sin') return 'Ningún contacto sin identificar';
  return `Ningún contacto de ${nombreMarca(marcas, clave)}`;
}

const VACIO_POR_VISTA = {
  citados: 'Ningún contacto con cita',
  descartados: 'Ningún contacto descartado',
};

/** El nombre de una procedencia (o de un grupo) para decirlo arriba. */
function nombreDeFiltro(clave, opciones, porProcedencia) {
  if (clave === 'campanas') return 'Campañas';
  if (clave === 'vias') return 'Otras vías';
  if (clave === 'sin') return 'Sin procedencia';
  const todas = [...(opciones?.campanas || []), ...(opciones?.vias || []), ...(porProcedencia || [])];
  return todas.find((o) => o.clave === clave)?.nombre || clave;
}

export default function LeadsPage() {
  const { user } = useAuth();
  // Borrar es de dirección. Enseñarle la papelera a quien va a recibir un 403
  // es ofrecerle algo que no puede hacer (revisión del 22/9).
  const puedeBorrar = isManagement(user);
  const [leads, setLeads] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [fallo, setFallo] = useState(null);
  const [truncado, setTruncado] = useState(0);
  // Los recuentos los da el servidor, sobre TODA la lista (M14, 23/9/2026).
  // Contados aquí sobre la página, pasados mil la pantalla decía una cifra y
  // el marcador de Indicadores otra.
  const [recuento, setRecuento] = useState({ pendientes: 0, contactados: 0 });
  const [busca, setBusca] = useState('');
  const [soloPendientes, setSoloPendientes] = useState(false);
  const [abierto, setAbierto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [nuevo, setNuevo] = useState(VACIO);
  // La pestaña de marca: todos | <clave de una marca> | sin. Filtra el
  // SERVIDOR, que es quien sabe la marca de todos (pasados mil, la página no
  // los trae).
  const [marca, setMarca] = useState('todos');
  // Las marcas dadas de alta (clave, nombre, color), en el orden de las
  // pestañas. Las manda el servidor; vacía, no hay pestañas.
  const [marcas, setMarcas] = useState([]);
  // Cuántos hay de cada marca, sobre toda la lista (lo cuenta el servidor).
  const [porMarca, setPorMarca] = useState(null);
  // La ficha abierta (el lead entero, tal y como lo manda el servidor).
  const [ficha, setFicha] = useState(null);
  const [asignando, setAsignando] = useState(false);
  const [params, setParams] = useSearchParams();
  // Qué filtro de estado: la lista de siempre, «Ya citados» o «Descartados».
  // Va en la URL (?vista=citados) para poder enlazarlo.
  const vista = VISTAS.includes(params.get('vista')) ? params.get('vista') : 'lista';
  const cambiarVista = (v) => {
    const p = new URLSearchParams(params);
    if (v === 'lista') p.delete('vista'); else p.set('vista', v);
    setParams(p);
  };
  // Cuántos hay en cada filtro de estado y en la vista elegida (el servidor).
  const [enFiltro, setEnFiltro] = useState({ citados: null, descartados: null, total: 0 });
  const [descartando, setDescartando] = useState(false);
  // Los marcados para descartar (o recuperar) en bloque, por id.
  const [marcados, setMarcados] = useState(() => new Set());
  const [confirmarLote, setConfirmarLote] = useState(false);
  const [motivoLote, setMotivoLote] = useState('');
  // La procedencia: el filtro y el orden van en la URL; los
  // números y las opciones para ponerla a mano, los manda el servidor.
  const procedencia = (params.get('procedencia') || '').trim();
  const ordenProcedencia = params.get('orden') === 'procedencia';
  const cambiarParam = (clave, valor) => {
    const p = new URLSearchParams(params);
    if (valor) p.set(clave, valor); else p.delete(clave);
    setParams(p);
  };
  const [porProcedencia, setPorProcedencia] = useState(null);
  const [opcionesProcedencia, setOpcionesProcedencia] = useState(SIN_OPCIONES_PROCEDENCIA);
  const [asignandoProcedencia, setAsignandoProcedencia] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const filtros = {};
      if (marca !== 'todos') filtros.marca = marca;
      if (vista !== 'lista') filtros.vista = vista;
      if (procedencia) filtros.procedencia = procedencia;
      if (ordenProcedencia) filtros.orden = 'procedencia';
      const r = await api.get('/api/leads', { params: filtros });
      const lista = r.data?.leads || [];
      setLeads(lista);
      // Lo marcado que ya no está en la lista (otra pestaña, otro filtro, o
      // ya descartado) deja de estar marcado: no se actúa sobre lo que no se ve.
      setMarcados((antes) => {
        const quedan = new Set(lista.map((l) => l.id));
        const siguen = [...antes].filter((id) => quedan.has(id));
        return siguen.length === antes.size ? antes : new Set(siguen);
      });
      setTruncado(r.data?.truncado ? r.data.total : 0);
      // Si un servidor viejo no los manda, se cuentan sobre lo que ha llegado.
      setRecuento({
        pendientes: r.data?.pendientes ?? lista.filter((l) => !l.contactado).length,
        contactados: r.data?.contactados ?? lista.filter((l) => l.contactado).length,
      });
      // Sin marcas dadas de alta (o un servidor viejo), no hay pestañas.
      const lasMarcas = Array.isArray(r.data?.marcas) ? r.data.marcas : [];
      setMarcas(lasMarcas);
      setPorMarca(r.data?.por_marca && lasMarcas.length
        ? { ...r.data.por_marca, completo: r.data.por_marca_completo !== false } : null);
      setEnFiltro({
        citados: r.data?.citados ?? null,
        descartados: r.data?.descartados ?? null,
        total: r.data?.total ?? lista.length,
      });
      // Un servidor viejo no manda la procedencia: entonces no hay filtro.
      setPorProcedencia(Array.isArray(r.data?.por_procedencia) ? r.data.por_procedencia : null);
      if (r.data?.procedencias?.campanas && r.data?.procedencias?.vias) {
        setOpcionesProcedencia(r.data.procedencias);
      }
      setFallo(null);
    } catch (e) {
      logError('leads', e);
      // No se deja la lista vacía sin más: eso se leería como «no hay nadie».
      // Y si es un 403, no se invita a reintentar algo que nunca va a salir.
      setFallo({
        texto: mensajeError(e, 'No se ha podido cargar la lista de contactos'),
        sinPermiso: e?.response?.status === 403,
      });
    } finally {
      setCargando(false);
    }
  }, [marca, vista, procedencia, ordenProcedencia]);

  useEffect(() => { cargar(); }, [cargar]);

  // Lo que dice el contador de al lado del buscador: el de la pestaña elegida
  // (y, con una procedencia elegida, el de esa procedencia: el servidor cuenta
  // las pestañas dentro de ella).
  const cifras = ((marca !== 'todos' || procedencia) && porMarca?.[marca]) || recuento;
  // Los números del filtro de procedencia: por grupo, y solo lo que tiene a
  // alguien (más lo elegido, aunque se haya quedado a cero).
  const filasProcedencia = porProcedencia || [];
  const sumaDe = (filas) => filas.reduce((s, f) => s + (f.total || 0), 0);
  const deCampanas = filasProcedencia.filter((f) => esCampana(f.clave));
  const deVias = filasProcedencia.filter((f) => !esCampana(f.clave));
  const conElegida = (filas, grupoCampanas) => {
    const salida = filas.filter((f) => f.total > 0);
    if (procedencia && !['campanas', 'vias'].includes(procedencia)
      && esCampana(procedencia) === grupoCampanas && !salida.some((f) => f.clave === procedencia)) {
      salida.push({ clave: procedencia, nombre: nombreDeFiltro(procedencia, opcionesProcedencia), total: 0 });
    }
    return salida;
  };
  // En «Ya citados» y «Descartados», cuántos hay en la pestaña elegida (el
  // servidor ya los cuenta con la pestaña aplicada).
  const enLaVista = enFiltro.total;
  const cuantosEn = (clave) => {
    if (porMarca?.[clave]) return porMarca[clave].total;
    return clave === 'todos' ? recuento.pendientes + recuento.contactados : null;
  };

  const visibles = useMemo(() => {
    const q = normalizaTexto(busca.trim());
    return leads.filter((l) => {
      if (soloPendientes && l.contactado) return false;
      if (!q) return true;
      // Se busca contra el número TAL Y COMO ESTÁ GUARDADO: quien teclea las
      // cifras seguidas tiene que encontrar al contacto igual.
      const campos = [l.nombre, l.telefono, l.email, l.motivo, l.origen,
        l.procedencia?.nombre].join(' ');
      return normalizaTexto(campos).includes(q);
    });
  }, [leads, busca, soloPendientes]);

  // Marcar varios (27/09/2026). La casilla de arriba marca o desmarca TODOS
  // los que se ven ahora (la pestaña, el filtro y la búsqueda de ahora).
  const marcadosVisibles = visibles.filter((l) => marcados.has(l.id)).length;
  const todosMarcados = visibles.length > 0 && marcadosVisibles === visibles.length;
  const alternar = (id) => setMarcados((antes) => {
    const nuevo = new Set(antes);
    if (nuevo.has(id)) nuevo.delete(id); else nuevo.add(id);
    return nuevo;
  });
  const alternarTodos = () => setMarcados((antes) => {
    const nuevo = new Set(antes);
    if (todosMarcados) visibles.forEach((l) => nuevo.delete(l.id));
    else visibles.forEach((l) => nuevo.add(l.id));
    return nuevo;
  });
  const quitarSeleccion = () => setMarcados(new Set());

  const enBloque = async (accion) => {
    if (descartando || !marcados.size) return;
    setDescartando(true);
    try {
      const cuerpo = { ids: [...marcados] };
      if (accion === 'descartar' && motivoLote.trim()) cuerpo.motivo = motivoLote.trim();
      const r = await api.post(`/api/leads/${accion}`, cuerpo);
      const hechos = accion === 'descartar' ? r.data?.descartados : r.data?.recuperados;
      const resumen = r.data?.resumen || `${hechos ?? 0} ${accion === 'descartar' ? 'descartados' : 'recuperados'}`;
      // Si alguno no se ha podido, se dice cuál es el motivo (ya lo estaba…).
      if ((r.data?.no || []).length) toast.warning(resumen);
      else toast.success(accion === 'descartar'
        ? `${resumen} Se recuperan en «Descartados».` : `${resumen} Vuelven a la lista.`);
      if (r.data?.aviso) toast.warning(r.data.aviso);
      setConfirmarLote(false);
      setMotivoLote('');
      quitarSeleccion();
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, accion === 'descartar' ? 'No se ha podido descartar' : 'No se ha podido recuperar'));
    } finally {
      setDescartando(false);
    }
  };

  const crear = async () => {
    if (guardando) return;
    setGuardando(true);
    try {
      // La marca y la procedencia solo van si quien lo apunta las sabe: si
      // no, las dirán su correo y «de dónde viene».
      const { marca: marcaNueva, procedencia: procedenciaNueva, ...datos } = nuevo;
      if (marcaNueva) datos.marca = marcaNueva;
      if (procedenciaNueva) datos.procedencia = procedenciaNueva;
      const r = await api.post('/api/leads', datos);
      const repes = r.data?.posibles_duplicados || [];
      toast.success(repes.length
        ? `Contacto añadido. Ojo: ya había ${repes.length} con ese teléfono o correo`
        : 'Contacto añadido');
      // Si algo no cabía, el servidor lo guarda recortado y lo dice (M9): lo
      // que dice el servidor se enseña, no se queda en la respuesta.
      if (r.data?.aviso) toast.warning(r.data.aviso);
      // Ya es paciente con cita: no sale en la lista, sino en «Ya citados».
      if (r.data?.lead?.citado) {
        toast.info(`Ya es paciente con cita (${r.data.lead.citado.paciente_nombre || 'ver ficha'}): está en «Ya citados»`);
      }
      setNuevo(VACIO);
      setAbierto(false);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido añadir'));
    } finally {
      setGuardando(false);
    }
  };

  const marcar = async (lead) => {
    try {
      const r = await api.put(`/api/leads/${lead.id}/contactado`, { contactado: !lead.contactado });
      // Con la lista sin refrescar, otra persona ya lo había marcado (o
      // deshecho): el servidor no lo toca y lo dice (M10). Se enseña, y la
      // recarga de abajo pone la fila como está de verdad.
      if (r.data?.ya_estaba) toast.info(r.data.aviso || 'Ya estaba así: no se ha cambiado nada');
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido marcar'));
    }
  };

  // Asignar la marca a mano, desde la ficha. Solo se ofrece cuando el correo
  // no la dice (lo decide el servidor: `puede_asignar_marca`). `null` la quita.
  const asignarMarca = async (lead, valor) => {
    if (asignando) return;
    setAsignando(true);
    try {
      const r = await api.put(`/api/leads/${lead.id}/marca`, { marca: valor });
      if (r.data?.ya_estaba) toast.info(r.data.aviso || 'Ya estaba así: no se ha cambiado nada');
      else toast.success(valor ? `Marca: ${nombreMarca(marcas, valor)}` : 'Marca quitada');
      if (r.data?.lead) setFicha(r.data.lead);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido asignar la marca'));
    } finally {
      setAsignando(false);
    }
  };

  // La procedencia a mano, desde la ficha. `null` la quita y vuelve a lo que
  // diga «de dónde viene».
  const asignarProcedencia = async (lead, valor) => {
    if (asignandoProcedencia) return;
    setAsignandoProcedencia(true);
    try {
      const r = await api.put(`/api/leads/${lead.id}/procedencia`, { procedencia: valor });
      if (r.data?.ya_estaba) toast.info(r.data.aviso || 'Ya estaba así: no se ha cambiado nada');
      else {
        toast.success(valor
          ? `Procedencia: ${r.data?.lead?.procedencia?.nombre || nombreDeFiltro(valor, opcionesProcedencia)}`
          : 'Procedencia puesta a mano quitada');
      }
      if (r.data?.lead) setFicha(r.data.lead);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido poner la procedencia'));
    } finally {
      setAsignandoProcedencia(false);
    }
  };

  // Descartar (27/09/2026): sale de la lista y de los números, pero NO se
  // borra; se recupera desde «Descartados».
  const descartar = async (lead, motivo) => {
    if (descartando) return;
    setDescartando(true);
    try {
      const r = await api.put(`/api/leads/${lead.id}/descartar`, { motivo });
      if (r.data?.ya_estaba) toast.info(r.data.aviso || 'Ya estaba descartado');
      else toast.success('Contacto descartado: ya no sale en la lista. Se recupera desde «Descartados».');
      if (r.data?.aviso && !r.data?.ya_estaba) toast.warning(r.data.aviso);
      setFicha(null);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido descartar'));
    } finally {
      setDescartando(false);
    }
  };

  const recuperar = async (lead) => {
    if (descartando) return;
    setDescartando(true);
    try {
      const r = await api.put(`/api/leads/${lead.id}/recuperar`);
      if (r.data?.ya_estaba) toast.info(r.data.aviso || 'No estaba descartado');
      else toast.success('Contacto recuperado: vuelve a la lista');
      setFicha(null);
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido recuperar'));
    } finally {
      setDescartando(false);
    }
  };

  const borrar = async (lead) => {
    const quien = lead.nombre || lead.telefono || lead.email || 'este contacto';
    if (!window.confirm(`¿Borrar «${quien}» de la lista?\n\nSi ya le has llamado, márcalo como contactado en vez de borrarlo: así queda el registro.`)) return;
    try {
      await api.delete(`/api/leads/${lead.id}`);
      toast.success('Contacto borrado');
      await cargar();
    } catch (e) {
      toast.error(mensajeError(e, 'No se ha podido borrar'));
    }
  };

  return (
    <div className="p-6 space-y-5" data-testid="leads-page">
      <PageHeader
        title="Leads / Contactos"
        subtitle="Quien ha preguntado por la clínica y todavía no es paciente"
        actions={
          <Button onClick={() => setAbierto(true)} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]"
            data-testid="leads-nuevo">
            <Plus className="w-4 h-4 mr-2" /> Añadir contacto
          </Button>
        }
      />

      {/* De qué marca viene: pestañas con su número. Solo si la clínica
          ha dado de alta alguna marca. */}
      {marcas.length ? (
        <div className="flex flex-wrap items-center gap-2" role="tablist" data-testid="leads-pestanas">
          {[{ clave: 'todos', nombre: 'Todos' }, ...marcas, { clave: 'sin', nombre: SIN_IDENTIFICAR }].map((p) => {
            const activa = marca === p.clave;
            const n = cuantosEn(p.clave);
            const color = colorDeMarca(marcas, p.clave);
            const estilo = estiloMarca(color);
            return (
              <button key={p.clave} type="button" role="tab" aria-selected={activa}
                data-testid={`leads-pestana-${p.clave}`}
                onClick={() => setMarca(p.clave)}
                style={activa && color ? estilo.chip : undefined}
                className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-sm transition-colors ${
                  activa
                    ? (color ? '' : 'bg-foreground text-background border-foreground')
                    : 'bg-background text-muted-foreground hover:text-foreground'
                } ${p.clave === 'sin' && !activa ? 'border-dashed' : ''}`}>
                {color ? <span aria-hidden="true" className="w-2 h-2 rounded-full" style={estilo.punto} /> : null}
                {p.nombre}
                {n !== null && n !== undefined ? (
                  <span className="tabular-nums font-semibold" data-testid={`leads-pestana-${p.clave}-n`}>{n}</span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}

      {/* De dónde viene: las campañas primero, cada una con su número;
          luego las demás vías. Se combina con la pestaña de marca. */}
      {porProcedencia && (procedencia || sumaDe(filasProcedencia) > 0) ? (
        <div className="flex flex-wrap items-center gap-1.5" data-testid="leads-procedencias"
          role="group" aria-label="Filtrar por procedencia">
          <span className="text-xs font-medium text-muted-foreground mr-1">Procedencia</span>
          <ChipProcedencia clave="" nombre="Todas" n={sumaDe(filasProcedencia)}
            activa={!procedencia} onClick={() => cambiarParam('procedencia', '')} />
          <ChipProcedencia clave="campanas" nombre="Campañas" n={sumaDe(deCampanas)} grupo
            activa={procedencia === 'campanas'} onClick={() => cambiarParam('procedencia', 'campanas')} />
          {conElegida(deCampanas, true).map((f) => (
            <ChipProcedencia key={f.clave} clave={f.clave} nombre={f.nombre} n={f.total}
              activa={procedencia === f.clave} onClick={() => cambiarParam('procedencia', f.clave)} />
          ))}
          <span className="mx-1 h-4 w-px bg-border" aria-hidden="true" />
          <ChipProcedencia clave="vias" nombre="Otras vías" n={sumaDe(deVias)} grupo
            activa={procedencia === 'vias'} onClick={() => cambiarParam('procedencia', 'vias')} />
          {conElegida(deVias, false).map((f) => (
            <ChipProcedencia key={f.clave} clave={f.clave} nombre={f.nombre} n={f.total}
              activa={procedencia === f.clave} onClick={() => cambiarParam('procedencia', f.clave)} />
          ))}
        </div>
      ) : null}

      {/* Lo que sale de la lista sin borrarse: cada uno en su filtro, con su
          número. */}
      <div className="flex flex-wrap items-center gap-2" data-testid="leads-vistas">
        {[
          { clave: 'lista', nombre: 'En la lista', icono: UserPlus, n: null },
          { clave: 'citados', nombre: 'Ya citados', icono: CalendarCheck, n: enFiltro.citados },
          { clave: 'descartados', nombre: 'Descartados', icono: Archive, n: enFiltro.descartados },
        ].map(({ clave, nombre, icono: Icono, n }) => {
          const activa = vista === clave;
          return (
            <button key={clave} type="button" aria-pressed={activa}
              data-testid={`leads-vista-${clave}`}
              onClick={() => cambiarVista(clave)}
              className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs transition-colors ${
                activa ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]' : 'bg-background text-muted-foreground hover:text-foreground'
              }`}>
              <Icono className="w-3.5 h-3.5" aria-hidden="true" />
              {nombre}
              {n !== null && n !== undefined ? (
                <span className="tabular-nums font-semibold" data-testid={`leads-vista-${clave}-n`}>({n})</span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Input value={busca} onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar por nombre, teléfono, correo o motivo"
          className="max-w-sm" data-testid="leads-buscar" />
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={soloPendientes} data-testid="leads-solo-pendientes"
            onChange={(e) => setSoloPendientes(e.target.checked)} />
          Solo sin contactar
        </label>
        <span className="text-sm text-muted-foreground" data-testid="leads-cuenta">
          {vista === 'citados' ? `${enLaVista} ya citados: ya son pacientes con cita`
            : vista === 'descartados' ? `${enLaVista} descartados: no salen en la lista ni en los números`
              : `${cifras.pendientes} sin contactar · ${cifras.contactados} contactados`}
          {porMarca && !porMarca.completo ? (
            <span className="text-amber-700" data-testid="leads-por-marca-cortado">
              {' '}· los números por marca no cuentan toda la lista
            </span>
          ) : null}
          {truncado ? (
            <span className="text-amber-700" data-testid="leads-truncado">
              {' '}· se enseñan los {leads.length} más recientes de {truncado}
            </span>
          ) : null}
        </span>
      </div>

      {marcados.size > 0 ? (
        <div className="flex flex-wrap items-center gap-3 rounded-md border border-[#2E6E5E]/30 bg-[#2E6E5E]/5 px-3 py-2 text-sm"
          data-testid="leads-seleccion" role="region" aria-label="Contactos marcados">
          <span className="font-medium" data-testid="leads-seleccion-n">
            {marcados.size} {marcados.size === 1 ? 'seleccionado' : 'seleccionados'}
          </span>
          {vista === 'descartados' ? (
            <Button size="sm" variant="outline" disabled={descartando}
              data-testid="leads-seleccion-recuperar" onClick={() => enBloque('recuperar')}>
              <ArchiveRestore className="w-4 h-4 mr-1" /> Recuperar
            </Button>
          ) : (
            <Button size="sm" variant="outline" disabled={descartando}
              data-testid="leads-seleccion-descartar" onClick={() => setConfirmarLote(true)}
              title="Van a «Descartados»: se pueden recuperar">
              <Archive className="w-4 h-4 mr-1" /> Eliminar de la lista
            </Button>
          )}
          <button type="button" className="underline text-muted-foreground hover:text-foreground"
            data-testid="leads-seleccion-quitar" onClick={quitarSeleccion}>
            Quitar selección
          </button>
        </div>
      ) : null}

      {fallo ? (
        <div className="text-sm text-red-700 border border-red-200 bg-red-50 rounded p-3"
          data-testid="leads-fallo">
          {fallo.texto}.{' '}
          {fallo.sinPermiso
            ? 'Esta lista es de administración y dirección.'
            : 'Vuelve a intentarlo; esta lista no está vacía, es que no se ha podido leer.'}
        </div>
      ) : cargando ? (
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
        </div>
      ) : visibles.length === 0 ? (
        <EmptyState icon={UserPlus}
          title={VACIO_POR_VISTA[vista]
            || (procedencia ? `Ningún contacto de «${nombreDeFiltro(procedencia, opcionesProcedencia, porProcedencia)}»` : null)
            || vacioDeMarca(marca, marcas)}
          description={vista === 'citados'
            ? 'Aquí salen los contactos que ya son pacientes con alguna cita (por su teléfono o su correo).'
            : vista === 'descartados'
              ? 'Aquí salen los contactos descartados, con su motivo, por si hay que recuperar alguno.'
              : 'Aquí van apareciendo quienes preguntan por la clínica.'} />
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-muted-foreground border-b">
                <th className="py-2 pr-3">
                  {/* Marcar todos los que se ven. En la misma columna que la
                      marca: la marca sigue siendo la primera. */}
                  <span className="inline-flex items-center gap-2">
                    <input type="checkbox" checked={todosMarcados} data-testid="leads-sel-todos"
                      aria-label="Marcar todos los que se ven"
                      ref={(el) => { if (el) el.indeterminate = marcadosVisibles > 0 && !todosMarcados; }}
                      onChange={alternarTodos} />
                    {marcas.length ? 'Marca' : null}
                  </span>
                </th>
                <th className="py-2 pr-3">Nombre</th>
                <th className="py-2 pr-3">Teléfono</th>
                <th className="py-2 pr-3">Correo</th>
                <th className="py-2 pr-3">Motivo de consulta</th>
                {/* Ordenar por procedencia: las campañas primero. */}
                <th className="py-2 pr-3" aria-sort={ordenProcedencia ? 'ascending' : 'none'}>
                  <button type="button" data-testid="leads-orden-procedencia" aria-pressed={ordenProcedencia}
                    onClick={() => cambiarParam('orden', ordenProcedencia ? '' : 'procedencia')}
                    title={ordenProcedencia
                      ? 'Quitar el orden: lo pendiente primero y lo más reciente arriba'
                      : 'Ordenar por procedencia: primero las campañas, luego las demás vías'}
                    className={`inline-flex items-center gap-1 hover:text-foreground ${ordenProcedencia ? 'text-foreground font-semibold' : ''}`}>
                    Procedencia
                    <ArrowDownWideNarrow className={`w-3.5 h-3.5 ${ordenProcedencia ? '' : 'opacity-40'}`} aria-hidden="true" />
                  </button>
                </th>
                <th className="py-2 pr-3">Entró</th>
                <th className="py-2 pr-3">Contactado</th>
                {vista === 'citados' ? <th className="py-2 pr-3">Ya citado</th> : null}
                {vista === 'descartados' ? <th className="py-2 pr-3">Descartado</th> : null}
                <th className="py-2" />
              </tr>
            </thead>
            <tbody>
              {visibles.map((l) => (
                <tr key={l.id} data-testid={`lead-${l.id}`}
                  className={`border-b last:border-0 ${l.contactado ? 'text-red-700 line-through' : ''}`}>
                  {/* De qué marca viene (si hay marcas). Pulsarla abre la ficha. */}
                  <td className="py-2 pr-3">
                    <span className="inline-flex items-center gap-2">
                      <input type="checkbox" checked={marcados.has(l.id)} data-testid={`lead-sel-${l.id}`}
                        aria-label={`Marcar ${l.nombre || l.email || 'este contacto'}`}
                        onChange={() => alternar(l.id)} />
                      {marcas.length ? (
                        <MarcaLead marca={l.marca} marcas={marcas} onClick={() => setFicha(l)}
                          testid={`lead-marca-${l.id}`}
                          title={l.marca_explicacion || 'Ver la ficha'} />
                      ) : null}
                    </span>
                  </td>
                  {/* Un hueco vacío es el dato: esa persona no lo dio. */}
                  <td className="py-2 pr-3 font-medium">{l.nombre}</td>
                  <td className="py-2 pr-3 tabular-nums">
                    {l.telefono ? (
                      <a href={`tel:${telefonoParaEnlace(l.telefono)}`}
                        className="inline-flex items-center gap-1 hover:underline">
                        {/* Con su prefijo, como en toda la casa: un número sin
                            prefijo no se puede llamar desde el móvil. */}
                        <Phone className="w-3 h-3" />{telefonoConPrefijo(l.telefono)}
                      </a>
                    ) : ''}
                  </td>
                  <td className="py-2 pr-3">
                    {l.email ? (
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:underline">
                        <Mail className="w-3 h-3" />{l.email}
                      </a>
                    ) : ''}
                  </td>
                  <td className="py-2 pr-3 max-w-md">{l.motivo}</td>
                  <td className="py-2 pr-3" data-testid={`lead-procedencia-${l.id}`}>
                    <ProcedenciaLead procedencia={l.procedencia} testid={`lead-procedencia-chip-${l.id}`} />
                  </td>
                  <td className="py-2 pr-3 text-xs text-muted-foreground no-underline">
                    {cuando(l.created_at)}{l.origen ? ` · ${l.origen}` : ''}
                  </td>
                  <td className="py-2 pr-3 text-xs" data-testid={`lead-contactado-${l.id}`}>
                    {l.contactado
                      ? `${l.contactado_via === 'conector' ? 'el conector' : (l.contactado_por_nombre || '')} ${cuando(l.contactado_at)}`.trim()
                      : ''}
                  </td>
                  {vista === 'citados' ? (
                    <td className="py-2 pr-3 text-xs no-underline" data-testid={`lead-citado-${l.id}`}>
                      <CitaDelLead citado={l.citado} />
                    </td>
                  ) : null}
                  {vista === 'descartados' ? (
                    <td className="py-2 pr-3 text-xs no-underline max-w-xs" data-testid={`lead-descartado-${l.id}`}>
                      <DescarteDelLead descartado={l.descartado} />
                    </td>
                  ) : null}
                  <td className="py-2 text-right whitespace-nowrap no-underline">
                    <Button variant="ghost" size="sm" onClick={() => setFicha(l)}
                      data-testid={`lead-ficha-abrir-${l.id}`} title="Ver la ficha">
                      <FileText className="w-4 h-4" />
                    </Button>
                    {vista === 'descartados' ? (
                      <Button variant="outline" size="sm" onClick={() => recuperar(l)} disabled={descartando}
                        data-testid={`lead-recuperar-${l.id}`} title="Vuelve a la lista">
                        <ArchiveRestore className="w-4 h-4 mr-1" /> Recuperar
                      </Button>
                    ) : (
                      <Button variant={l.contactado ? 'ghost' : 'outline'} size="sm"
                        onClick={() => marcar(l)} data-testid={`lead-marcar-${l.id}`}
                        title={l.contactado ? 'Deshacer: vuelve a la lista de pendientes' : 'Ya he hablado con esta persona'}>
                        {l.contactado ? <Undo2 className="w-4 h-4" /> : <><Check className="w-4 h-4 mr-1" /> Contactado</>}
                      </Button>
                    )}
                    {puedeBorrar && (
                      <Button variant="ghost" size="sm" onClick={() => borrar(l)}
                        data-testid={`lead-borrar-${l.id}`} title="Borrar de la lista">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={abierto} onOpenChange={setAbierto}>
        <DialogContent className="max-w-lg" data-testid="leads-dialogo">
          <DialogHeader><DialogTitle>Añadir contacto</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">
            Pon solo lo que esa persona haya dado. Lo que no sepas, déjalo en blanco:
            un hueco vacío dice «no lo dio», y cualquier relleno dice algo que no ha dicho.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label>Nombre</Label>
              <Input value={nuevo.nombre} data-testid="lead-nombre"
                onChange={(e) => setNuevo({ ...nuevo, nombre: e.target.value })} />
            </div>
            <div>
              <Label>Teléfono</Label>
              <Input value={nuevo.telefono} data-testid="lead-telefono"
                onChange={(e) => setNuevo({ ...nuevo, telefono: e.target.value })} />
            </div>
            <div>
              <Label>Correo</Label>
              <Input value={nuevo.email} data-testid="lead-email"
                onChange={(e) => setNuevo({ ...nuevo, email: e.target.value })} />
            </div>
            <div>
              <Label>De dónde viene</Label>
              <Input value={nuevo.origen} placeholder="llamada, Instagram, web…"
                data-testid="lead-origen"
                onChange={(e) => setNuevo({ ...nuevo, origen: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Motivo de la consulta</Label>
            <Input value={nuevo.motivo} data-testid="lead-motivo"
              onChange={(e) => setNuevo({ ...nuevo, motivo: e.target.value })} />
          </div>
          {/* De dónde viene: opcional. Si viene de una campaña de un
              tratamiento, aquí se dice; si no, la dirá «de dónde viene». */}
          <div>
            <Label htmlFor="lead-procedencia-nueva">Procedencia (si la sabes)</Label>
            <SelectorProcedencia id="lead-procedencia-nueva" testid="lead-procedencia-nueva"
              value={nuevo.procedencia} opciones={opcionesProcedencia}
              vacio="No lo sé: la dirá «de dónde viene»"
              onChange={(v) => setNuevo({ ...nuevo, procedencia: v })} />
          </div>
          {/* Opcional, y solo si hay marcas: si llamó al teléfono de una,
              quien lo apunta lo sabe. Si no, se deja y la marca la dirá su
              correo o «de dónde viene». */}
          {marcas.length ? (
            <div>
              <Label htmlFor="lead-marca-nueva">Marca (si la sabes)</Label>
              <select id="lead-marca-nueva" value={nuevo.marca} data-testid="lead-marca-nueva"
                onChange={(e) => setNuevo({ ...nuevo, marca: e.target.value })}
                className="mt-1 block w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                <option value="">No lo sé: la dirá su correo si escribe</option>
                {marcas.map((m) => <option key={m.clave} value={m.clave}>{m.nombre}</option>)}
              </select>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
            <Button onClick={crear} disabled={guardando}
              className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="lead-guardar">
              {guardando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Añadir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Eliminar varios de la lista: se DESCARTAN, y se dice antes. */}
      <Dialog open={confirmarLote} onOpenChange={(abierta) => { if (!abierta) setConfirmarLote(false); }}>
        <DialogContent className="max-w-md" data-testid="leads-lote-dialogo">
          <DialogHeader>
            <DialogTitle>
              ¿Descartar {marcados.size} {marcados.size === 1 ? 'lead' : 'leads'}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground" data-testid="leads-lote-texto">
            Saldrán de la lista; se pueden recuperar en Descartados.
          </p>
          <div>
            <Label htmlFor="leads-lote-motivo">Motivo (opcional)</Label>
            <Input id="leads-lote-motivo" value={motivoLote} maxLength={200}
              data-testid="leads-lote-motivo" placeholder="Ya no vale, spam, repetido…"
              onChange={(e) => setMotivoLote(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmarLote(false)}
              data-testid="leads-lote-cancelar">Cancelar</Button>
            <Button onClick={() => enBloque('descartar')} disabled={descartando}
              data-testid="leads-lote-confirmar">
              {descartando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
              Descartar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <FichaLead lead={ficha} onClose={() => setFicha(null)} marcas={marcas}
        asignando={asignando} onAsignar={asignarMarca}
        opcionesProcedencia={opcionesProcedencia}
        asignandoProcedencia={asignandoProcedencia} onAsignarProcedencia={asignarProcedencia}
        descartando={descartando} onDescartar={descartar} onRecuperar={recuperar} />
    </div>
  );
}

/** Un botón del filtro de procedencia, con su número. Los de grupo
 *  («Campañas», «Otras vías») van en negrita: cogen el grupo entero. */
function ChipProcedencia({ clave, nombre, n, activa, onClick, grupo = false }) {
  const testid = `leads-procedencia-${clave || 'todas'}`;
  const estilo = clave && !grupo ? estiloProcedencia(clave) : 'bg-background text-muted-foreground';
  return (
    <button type="button" aria-pressed={activa} data-testid={testid} onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs transition-colors ${
        activa ? 'bg-[#2E6E5E] text-white border-[#2E6E5E]' : `${estilo} hover:text-foreground`
      } ${grupo ? 'font-semibold' : ''}`}>
      {nombre}
      <span className="tabular-nums font-semibold" data-testid={`${testid}-n`}>{n}</span>
    </button>
  );
}

/** La cita por la que el contacto ya es paciente: su ficha y cuándo. */
function CitaDelLead({ citado }) {
  if (!citado) return null;
  return (
    <span>
      <Link to={`/pacientes/${citado.paciente_id}`} className="text-[#2E6E5E] hover:underline font-medium"
        data-testid={`lead-citado-paciente-${citado.paciente_id}`}>
        {citado.paciente_nombre || 'Ver ficha'}
      </Link>
      {' · '}
      {citado.proxima ? 'cita el ' : 'última cita el '}
      {fechaDeCita(citado.fecha, citado.hora)}
    </span>
  );
}

/** Por qué se descartó, quién y cuándo. */
function DescarteDelLead({ descartado }) {
  if (!descartado) return null;
  return (
    <span>
      <span className="text-foreground">{descartado.motivo}</span>
      <span className="block text-muted-foreground">
        {[descartado.por_nombre, cuando(descartado.at)].filter(Boolean).join(' · ')}
      </span>
    </span>
  );
}

function Dato({ etiqueta, children, testid }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-2 py-1 text-sm">
      <span className="text-muted-foreground">{etiqueta}</span>
      <span data-testid={testid} className="break-words">{children}</span>
    </div>
  );
}

/**
 * La ficha de un lead: sus datos y, si hay marcas, de qué marca viene y POR
 * QUÉ. Si escribió al correo de varias se dice (cuenta el primero). Si el
 * correo no dice la marca, aquí se asigna a mano; el servidor apunta quién y
 * cuándo.
 */
function FichaLead({
  lead, marcas, onClose, asignando, onAsignar, descartando, onDescartar, onRecuperar,
  opcionesProcedencia, asignandoProcedencia, onAsignarProcedencia,
}) {
  const [motivo, setMotivo] = useState('');
  const [pidiendo, setPidiendo] = useState(false);
  // Otro contacto, otro motivo: lo escrito para el anterior no se arrastra.
  useEffect(() => { setMotivo(''); setPidiendo(false); }, [lead?.id]);
  if (!lead) return null;
  const buzones = lead.buzones || [];
  const aMano = lead.marca_regla === 'a_mano';
  const procedencia = lead.procedencia || null;
  const procedenciaAMano = procedencia?.regla === 'a_mano';
  return (
    <Dialog open onOpenChange={(abierta) => { if (!abierta) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="lead-ficha">
        <DialogHeader><DialogTitle>Ficha del contacto</DialogTitle></DialogHeader>

        {marcas?.length ? (
          <div className="rounded-md border p-3 space-y-2" data-testid="lead-ficha-marca">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Viene de</span>
              <MarcaLead marca={lead.marca} marcas={marcas} testid="lead-ficha-chip" />
            </div>
            {lead.marca_explicacion ? (
              <p className="text-xs text-muted-foreground" data-testid="lead-ficha-explicacion">
                {lead.marca_explicacion}
              </p>
            ) : null}
            {lead.escribio_a_varias ? (
              <p className="text-xs font-medium text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1"
                data-testid="lead-ficha-varias">
                Escribió al correo de varias marcas. La marca es la del primero.
              </p>
            ) : null}
            {buzones.length ? (
              <ul className="text-xs space-y-1" data-testid="lead-ficha-buzones">
                {buzones.map((b, i) => (
                  <li key={b.buzon} className="flex items-center gap-2">
                    <span className="text-muted-foreground w-16">{i === 0 ? 'Primero' : 'Después'}</span>
                    <MarcaLead marca={b.marca} marcas={marcas} />
                    {cuando(b.desde) ? <span className="text-muted-foreground">{cuando(b.desde)}</span> : null}
                  </li>
                ))}
              </ul>
            ) : null}
            {lead.puede_asignar_marca ? (
              <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="lead-ficha-asignar">
                <span className="text-xs text-muted-foreground">
                  {aMano ? 'Cambiar a mano:' : 'Asignar a mano:'}
                </span>
                {marcas.map((m) => (
                  <Button key={m.clave} type="button" size="sm" variant="outline"
                    disabled={asignando || (aMano && lead.marca === m.clave)}
                    data-testid={`lead-ficha-asignar-${m.clave}`}
                    onClick={() => onAsignar(lead, m.clave)}>
                    {m.nombre}
                  </Button>
                ))}
                {aMano ? (
                  <Button type="button" size="sm" variant="ghost" disabled={asignando}
                    data-testid="lead-ficha-quitar-marca" onClick={() => onAsignar(lead, null)}>
                    Quitar
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground" data-testid="lead-ficha-sin-asignar">
                La marca la dice su correo: no se cambia a mano.
              </p>
            )}
          </div>
        ) : null}

        {/* De dónde viene: qué procedencia tiene y por qué, y ponerla a mano
            (manda sobre todo). */}
        {procedencia ? (
          <div className="rounded-md border p-3 space-y-2" data-testid="lead-ficha-procedencia">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Procedencia</span>
              <ProcedenciaLead procedencia={procedencia} testid="lead-ficha-procedencia-chip" />
            </div>
            {procedencia.explicacion ? (
              <p className="text-xs text-muted-foreground" data-testid="lead-ficha-procedencia-explicacion">
                {procedencia.explicacion}
              </p>
            ) : null}
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Label htmlFor="lead-ficha-procedencia-elegir" className="text-xs text-muted-foreground font-normal">
                {procedenciaAMano ? 'Cambiar a mano:' : 'Poner a mano:'}
              </Label>
              <SelectorProcedencia id="lead-ficha-procedencia-elegir" testid="lead-ficha-procedencia-elegir"
                className="w-auto max-w-full" value={procedenciaAMano ? procedencia.clave : ''}
                actual={procedenciaAMano ? procedencia : null} opciones={opcionesProcedencia}
                vacio={procedenciaAMano ? 'Elige otra…' : 'Elige…'} disabled={asignandoProcedencia}
                onChange={(v) => { if (v) onAsignarProcedencia(lead, v); }} />
              {procedenciaAMano ? (
                <Button type="button" size="sm" variant="ghost" disabled={asignandoProcedencia}
                  data-testid="lead-ficha-quitar-procedencia" onClick={() => onAsignarProcedencia(lead, null)}>
                  Quitar
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}

        {/* Lo que esa persona no dio sale en blanco, como en la lista. */}
        <div>
          <Dato etiqueta="Nombre" testid="lead-ficha-nombre">{lead.nombre}</Dato>
          <Dato etiqueta="Teléfono" testid="lead-ficha-telefono">
            {lead.telefono ? telefonoConPrefijo(lead.telefono) : ''}
          </Dato>
          <Dato etiqueta="Correo" testid="lead-ficha-email">{lead.email}</Dato>
          <Dato etiqueta="Motivo" testid="lead-ficha-motivo">{lead.motivo}</Dato>
          <Dato etiqueta="De dónde viene" testid="lead-ficha-origen">{lead.origen}</Dato>
          <Dato etiqueta="Entró" testid="lead-ficha-entro">
            {`${cuando(lead.created_at)}${lead.created_by_nombre ? ` · apuntado por ${lead.created_by_nombre}` : ''}`}
          </Dato>
          <Dato etiqueta="Contactado" testid="lead-ficha-contactado">
            {lead.contactado
              ? `${lead.contactado_via === 'conector' ? 'el conector' : (lead.contactado_por_nombre || '')} ${cuando(lead.contactado_at)}`.trim()
              : ''}
          </Dato>
        </div>

        {lead.citado ? (
          <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm" data-testid="lead-ficha-citado">
            <span className="font-medium">Ya es paciente con cita: </span>
            <CitaDelLead citado={lead.citado} />
          </div>
        ) : null}

        {/* Descartar: no se borra; se recupera desde «Descartados». */}
        {lead.descartado ? (
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm space-y-2"
            data-testid="lead-ficha-descartado">
            <div>
              <span className="font-medium">Descartado: </span>
              <DescarteDelLead descartado={lead.descartado} />
            </div>
            <Button type="button" size="sm" variant="outline" disabled={descartando}
              data-testid="lead-ficha-recuperar" onClick={() => onRecuperar(lead)}>
              <ArchiveRestore className="w-4 h-4 mr-1" /> Recuperar
            </Button>
          </div>
        ) : pidiendo ? (
          <div className="rounded-md border p-3 space-y-2" data-testid="lead-ficha-descartar-form">
            <Label htmlFor="lead-ficha-descartar-motivo">¿Por qué se descarta?</Label>
            <Input id="lead-ficha-descartar-motivo" value={motivo} maxLength={200}
              data-testid="lead-ficha-descartar-motivo" placeholder="Ya no vale, spam, repetido…"
              onChange={(e) => setMotivo(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && motivo.trim()) onDescartar(lead, motivo.trim()); }} />
            <p className="text-xs text-muted-foreground">
              Sale de la lista y de los números, pero no se borra: se recupera desde «Descartados».
            </p>
            <div className="flex gap-2">
              <Button type="button" size="sm" disabled={descartando || !motivo.trim()}
                data-testid="lead-ficha-descartar-confirmar" onClick={() => onDescartar(lead, motivo.trim())}>
                {descartando ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Archive className="w-4 h-4 mr-1" />}
                Descartar
              </Button>
              <Button type="button" size="sm" variant="ghost" onClick={() => setPidiendo(false)}>Cancelar</Button>
            </div>
          </div>
        ) : null}

        <DialogFooter>
          {!lead.descartado && !pidiendo ? (
            <Button variant="ghost" onClick={() => setPidiendo(true)} className="mr-auto"
              data-testid="lead-ficha-descartar">
              <Archive className="w-4 h-4 mr-1" /> Descartar
            </Button>
          ) : null}
          <Button variant="outline" onClick={onClose} data-testid="lead-ficha-cerrar">Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
